import React, { useState, useEffect } from 'react';
import {
  Smartphone, Plus, Trash2, Check, X, RefreshCw, Search, Sparkles,
  AlertCircle, Award, Zap, Target, Activity, Clock, CheckCircle2,
  XCircle, Wifi
} from 'lucide-react';
import api from '../services/api';

/**
 * SamsaraConfigV2 - Modelo correcto:
 *  Tab 1: Auto-detectado (lo principal - Samsara por heurística multi-MDVR)
 *  Tab 2: SIM cellular (marcar MDVRs con SIM)
 *  Tab 3: Rangos manuales
 *  Tab 4: Health del cron
 *  Tab 5: Probar IP
 *
 * REEMPLAZA al 16_SamsaraConfigPro.js
 */
const SamsaraV2 = () => {
  const [tab, setTab] = useState('auto');
  const [autoDet, setAutoDet]       = useState([]);
  const [simMdvrs, setSimMdvrs]     = useState([]);
  const [allMdvrs, setAllMdvrs]     = useState([]);
  const [ranges, setRanges]         = useState([]);
  const [cronStatus, setCronStatus] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [running, setRunning]       = useState(false);

  const [newCidr, setNewCidr] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [testIp, setTestIp]   = useState('');
  const [testResult, setTestResult] = useState(null);
  const [searchMdvr, setSearchMdvr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [a, s, m, r, c] = await Promise.all([
        api.get('/api/samsara/auto-detected?min_mdvrs=2'),
        api.get('/api/mdvr/sim/list'),
        api.get('/api/mdvr/status'),
        api.get('/api/samsara/ranges'),
        api.get('/api/cron/health'),
      ]);
      setAutoDet(a.data || []);
      setSimMdvrs(s.data || []);
      setAllMdvrs(m.data || []);
      setRanges(r.data || []);
      setCronStatus(c.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  // Acciones
  const runAutoLearn = async () => {
    setRunning(true);
    try {
      const r = await api.post('/api/samsara/auto-learn');
      const promoted = r.data?.details?.promoted || [];
      alert(promoted.length > 0
        ? `✅ ${promoted.length} subredes promovidas:\n${promoted.map(p => `${p.subnet} (${p.mdvrs} MDVRs)`).join('\n')}`
        : 'No hay subredes nuevas para promover ahora');
      load();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally { setRunning(false); }
  };

  const toggleSim = async (deviceId, current) => {
    await api.put(`/api/mdvr/${deviceId}/sim/mark`, { has_sim_backup: !current });
    load();
  };

  const promoteOne = async (subnet) => {
    await api.post('/api/samsara/promote', {
      subnet, descripcion: 'Promovido manualmente desde auto-detect'
    });
    load();
  };

  const addRange = async () => {
    if (!newCidr.trim()) return;
    try {
      await api.post('/api/samsara/ranges', { cidr: newCidr, descripcion: newDesc });
      setNewCidr(''); setNewDesc(''); load();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || 'CIDR inválido')); }
  };

  const deleteRange = async (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    await api.delete(`/api/samsara/ranges/${id}`);
    load();
  };

  const testClassification = async () => {
    if (!testIp.trim()) return;
    try {
      const r = await api.get(`/api/samsara/test-ip?ip=${encodeURIComponent(testIp)}`);
      setTestResult(r.data);
    } catch (e) { setTestResult({ error: e.response?.data?.detail || 'Error' }); }
  };

  const mdvrsFiltered = allMdvrs.filter(m =>
    !searchMdvr ||
    (m.numero_unidad || '').toLowerCase().includes(searchMdvr.toLowerCase()) ||
    (m.nombre || '').toLowerCase().includes(searchMdvr.toLowerCase())
  );

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><Smartphone size={26} /> Samsara · Auto-aprendizaje</h1>
          <p style={S.subtitle}>
            Detección automática multi-MDVR + monitoreo cron
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <CronBadge status={cronStatus} />
          <button style={S.refreshBtn} onClick={load}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </header>

      <div style={S.tabs}>
        <Tab active={tab === 'auto'}      onClick={() => setTab('auto')}      icon={<Sparkles size={14}/>}     label={`Auto-detectado (${autoDet.filter(a => !a.already_promoted).length})`} />
        <Tab active={tab === 'sim'}       onClick={() => setTab('sim')}       icon={<Smartphone size={14}/>}    label={`SIM cellular (${simMdvrs.length})`} />
        <Tab active={tab === 'ranges'}    onClick={() => setTab('ranges')}    icon={<Target size={14}/>}        label={`Rangos (${ranges.length})`} />
        <Tab active={tab === 'cron'}      onClick={() => setTab('cron')}      icon={<Activity size={14}/>}      label="Cron health" />
        <Tab active={tab === 'test'}      onClick={() => setTab('test')}      icon={<Search size={14}/>}        label="Probar IP" />
      </div>

      {/* ============ TAB AUTO-DETECTADO (principal) ============ */}
      {tab === 'auto' && (
        <>
          <div style={{ ...S.card, background: '#eff6ff', borderLeft: '4px solid #3b82f6' }}>
            <h3 style={{ marginTop: 0, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Zap size={18}/> ¿Cómo funciona?
            </h3>
            <p style={{ margin: '4px 0', color: '#374151' }}>
              <strong>Heurística:</strong> si <b>2+ MDVRs distintos</b> aparecen desde la misma subred /24
              que <b>NO</b> es sucursal Mikrotik, esa subred es Samsara confirmado (solo Samsara hace NAT compartido).
            </p>
            <p style={{ margin: '4px 0', color: '#374151' }}>
              El cron corre cada hora y promueve automáticamente. También puedes ejecutarlo manualmente:
            </p>
            <button style={{ ...S.btnPrimary, marginTop: 8 }} onClick={runAutoLearn} disabled={running}>
              {running ? <RefreshCw size={16} className="spin"/> : <Zap size={16}/>}
              {running ? 'Ejecutando...' : 'Ejecutar auto-learn ahora'}
            </button>
          </div>

          <div style={S.card}>
            <h3 style={{ marginTop: 0 }}>Subredes detectadas ({autoDet.length})</h3>
            {autoDet.length === 0 ? (
              <p style={S.help}>
                Aún no se detectan subredes con 2+ MDVRs. Espera a que más MDVRs se conecten al hotspot.
              </p>
            ) : (
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Subred /24</th>
                    <th style={S.th}>MDVRs distintos</th>
                    <th style={S.th}>Conexiones</th>
                    <th style={S.th}>Confianza</th>
                    <th style={S.th}>Primera</th>
                    <th style={S.th}>Última</th>
                    <th style={S.th}>Estado</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {autoDet.map(d => (
                    <tr key={d.subnet_24}>
                      <td style={{ ...S.td, fontFamily: 'monospace', fontWeight: 600 }}>
                        {d.subnet_24}
                      </td>
                      <td style={S.td}>
                        <span style={S.devBadge}>{d.mdvrs_distintos}</span>
                      </td>
                      <td style={S.td}>{d.total_conexiones}</td>
                      <td style={S.td}><ConfBadge value={d.confidence}/></td>
                      <td style={{ ...S.td, fontSize: 12 }}>
                        {d.first_seen ? new Date(d.first_seen).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                      </td>
                      <td style={{ ...S.td, fontSize: 12 }}>
                        {d.last_seen ? new Date(d.last_seen).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                      </td>
                      <td style={S.td}>
                        {d.already_promoted
                          ? <span style={{ ...S.badge, background:'#d1fae5', color:'#065f46' }}>
                              <CheckCircle2 size={12}/> Promovida
                            </span>
                          : <span style={{ ...S.badge, background:'#fef3c7', color:'#92400e' }}>
                              Pendiente
                            </span>}
                      </td>
                      <td style={S.td}>
                        {!d.already_promoted && (
                          <button style={S.btnAccept} onClick={() => promoteOne(d.subnet_24)}>
                            <Zap size={14}/> Promover
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ============ TAB SIM ============ */}
      {tab === 'sim' && (
        <>
          <div style={{ ...S.card, background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
            <h3 style={{ marginTop: 0, color: '#92400e' }}>📱 ¿Qué es esto?</h3>
            <p style={{ color: '#374151' }}>
              Marca los MDVRs que <b>tienen SIM cellular</b> de respaldo (cuando no hay WiFi).
              Cuando se conecten desde IPs no-sucursal y NO sean Samsara, se clasificarán como <b>datos celular</b>.
            </p>
          </div>

          {simMdvrs.length > 0 && (
            <div style={S.card}>
              <h3 style={{ marginTop: 0 }}>✅ MDVRs con SIM ({simMdvrs.length})</h3>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Unidad</th>
                    <th style={S.th}>IP actual</th>
                    <th style={S.th}>Tipo conexión</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {simMdvrs.map(m => (
                    <tr key={m.device_id}>
                      <td style={{ ...S.td, fontWeight: 600 }}>{m.numero_unidad}</td>
                      <td style={{ ...S.td, fontFamily: 'monospace' }}>{m.ip_address || '-'}</td>
                      <td style={S.td}><ConnTypeBadge type={m.connection_type} /></td>
                      <td style={S.td}>
                        <button style={S.btnIgnore} onClick={() => toggleSim(m.device_id, true)}>
                          <X size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={S.card}>
            <h3 style={{ marginTop: 0 }}>📋 Marcar MDVRs con SIM</h3>
            <input placeholder="Buscar..." value={searchMdvr}
                   onChange={e => setSearchMdvr(e.target.value)}
                   style={{ ...S.input, width: '100%', maxWidth: 400, marginBottom: 12 }}/>
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              <table style={S.table}>
                <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                  <tr>
                    <th style={S.th}>SIM</th>
                    <th style={S.th}>Unidad</th>
                    <th style={S.th}>Estado</th>
                    <th style={S.th}>Conexión</th>
                  </tr>
                </thead>
                <tbody>
                  {mdvrsFiltered.map(m => {
                    const isSim = simMdvrs.some(x => x.device_id === m.device_id);
                    return (
                      <tr key={m.device_id} style={{ background: isSim ? '#fffbeb' : 'transparent' }}>
                        <td style={S.td}>
                          <input type="checkbox" checked={isSim}
                                 onChange={() => toggleSim(m.device_id, isSim)}
                                 style={{ cursor: 'pointer', width: 18, height: 18 }}/>
                        </td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{m.numero_unidad}</td>
                        <td style={S.td}>
                          {m.is_current
                            ? <span style={{ color:'#10b981', fontWeight:600 }}>● Online</span>
                            : <span style={{ color:'#9ca3af' }}>○ Offline</span>}
                        </td>
                        <td style={S.td}><ConnTypeBadge type={m.connection_type} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ============ TAB RANGOS ============ */}
      {tab === 'ranges' && (
        <>
          <div style={S.card}>
            <h3 style={{ marginTop: 0 }}>Agregar rango manual</h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input placeholder="ej: 100.64.0.0/10" value={newCidr}
                     onChange={e => setNewCidr(e.target.value)}
                     style={{ ...S.input, flex: '1 1 200px' }}/>
              <input placeholder="Descripción" value={newDesc}
                     onChange={e => setNewDesc(e.target.value)}
                     style={{ ...S.input, flex: '2 1 300px' }}/>
              <button style={S.btnPrimary} onClick={addRange}><Plus size={16}/> Agregar</button>
            </div>
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>CIDR</th>
                  <th style={S.th}>Descripción</th>
                  <th style={S.th}>Activo</th>
                  <th style={S.th}>Creado</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {ranges.length === 0 && (
                  <tr><td colSpan={5} style={S.empty}>Sin rangos. Espera al cron o crea manualmente.</td></tr>
                )}
                {ranges.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontWeight: 600 }}>{r.cidr}</td>
                    <td style={S.td}>{r.descripcion || '-'}</td>
                    <td style={S.td}>{r.activo ? '✅' : '❌'}</td>
                    <td style={{ ...S.td, fontSize: 12, color: '#6b7280' }}>
                      {new Date(r.created_at).toLocaleDateString('es-MX')}
                    </td>
                    <td style={S.td}>
                      <button style={S.btnDanger} onClick={() => deleteRange(r.id)}>
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ============ TAB CRON HEALTH ============ */}
      {tab === 'cron' && (
        <CronStatusView />
      )}

      {/* ============ TAB TEST IP ============ */}
      {tab === 'test' && (
        <div style={S.card}>
          <h3 style={{ marginTop: 0 }}>Probar clasificación</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <input placeholder="ej: 100.64.50.123" value={testIp}
                   onChange={e => setTestIp(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && testClassification()}
                   style={{ ...S.input, flex: 1, fontFamily: 'monospace' }}/>
            <button style={S.btnPrimary} onClick={testClassification}>
              <Search size={16}/> Clasificar
            </button>
          </div>
          {testResult && (
            <div style={S.resultBox}>
              {testResult.error
                ? <div style={{ color: '#dc2626' }}><AlertCircle size={20}/> {testResult.error}</div>
                : <>
                    <div style={S.resultRow}>
                      <span style={S.resultLabel}>Tipo:</span>
                      <ConnTypeBadge type={testResult.connection_type} />
                    </div>
                    <div style={S.resultRow}>
                      <span style={S.resultLabel}>Sucursal:</span>
                      <strong>{testResult.sucursal}</strong>
                    </div>
                    {testResult.samsara_match && (
                      <div style={S.resultRow}>
                        <span style={S.resultLabel}>Match:</span>
                        <code>{testResult.samsara_match}</code>
                      </div>
                    )}
                  </>}
            </div>
          )}
        </div>
      )}

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

// ============ Cron Status View ============
const CronStatusView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get('/api/cron/status?job_name=samsara_auto_learn');
        setData(r.data);
      } finally { setLoading(false); }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <div style={S.card}>Cargando...</div>;

  return (
    <>
      {data?.stats_30d?.map(s => (
        <div key={s.job_name} style={S.card}>
          <h3 style={{ marginTop: 0 }}>📊 {s.job_name}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <Kpi label="Última ejecución exitosa" value={s.last_success ? new Date(s.last_success).toLocaleString('es-MX') : 'Nunca'} small />
            <Kpi label="Última ejecución" value={s.last_run ? new Date(s.last_run).toLocaleString('es-MX') : 'Nunca'} small />
            <Kpi label="Exitosas (30d)" value={s.total_success} color="#10b981" />
            <Kpi label="Fallidas (30d)" value={s.total_failed || 0} color={s.total_failed > 0 ? '#dc2626' : '#9ca3af'} />
          </div>
        </div>
      ))}

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>📜 Historial reciente</h3>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Inicio</th>
              <th style={S.th}>Job</th>
              <th style={S.th}>Estado</th>
              <th style={S.th}>Items</th>
              <th style={S.th}>Duración</th>
              <th style={S.th}>Detalles</th>
            </tr>
          </thead>
          <tbody>
            {(data?.recent_runs || []).length === 0 && (
              <tr><td colSpan={6} style={S.empty}>El cron aún no ha corrido. Verifica config.</td></tr>
            )}
            {(data?.recent_runs || []).map(r => {
              const dur = r.finished_at && r.started_at
                ? ((new Date(r.finished_at) - new Date(r.started_at)) / 1000).toFixed(1) + 's'
                : '-';
              return (
                <tr key={r.id}>
                  <td style={{ ...S.td, fontSize: 12 }}>
                    {new Date(r.started_at).toLocaleString('es-MX')}
                  </td>
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{r.job_name}</td>
                  <td style={S.td}>
                    {r.success === 1
                      ? <span style={{ color: '#10b981' }}><CheckCircle2 size={14}/> OK</span>
                      : r.success === 0
                        ? <span style={{ color: '#dc2626' }}><XCircle size={14}/> Falló</span>
                        : <span style={{ color: '#9ca3af' }}>En curso</span>}
                  </td>
                  <td style={S.td}>{r.items_processed}</td>
                  <td style={S.td}>{dur}</td>
                  <td style={{ ...S.td, fontSize: 11, color: '#6b7280', maxWidth: 300 }}>
                    {r.error_msg || (r.details_json ? r.details_json.substring(0, 100) + '...' : '')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ============ Helpers ============
const Tab = ({ active, onClick, icon, label }) => (
  <button onClick={onClick}
          style={{ ...S.tab, ...(active ? S.tabActive : {}) }}>
    {icon} {label}
  </button>
);

const Kpi = ({ label, value, color = '#374151', small }) => (
  <div style={{ background: '#f9fafb', padding: 12, borderRadius: 6 }}>
    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: small ? 13 : 22, fontWeight: 700, color }}>{value}</div>
  </div>
);

const CronBadge = ({ status }) => {
  const job = status?.samsara_auto_learn;
  if (!job) return null;
  const healthy = job.healthy;
  return (
    <div title={`Última ejecución: ${job.last_run || 'nunca'}`}
         style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                  background: healthy ? '#d1fae5' : '#fee2e2',
                  color: healthy ? '#065f46' : '#991b1b',
                  borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
      <Activity size={14}/>
      Cron: {healthy ? 'OK' : (job.minutes_since == null ? 'No ha corrido' : `${job.minutes_since}min sin correr`)}
    </div>
  );
};

const ConfBadge = ({ value }) => {
  const color = value >= 80 ? '#10b981' : value >= 60 ? '#3b82f6' : '#f59e0b';
  const bg    = value >= 80 ? '#d1fae5' : value >= 60 ? '#dbeafe' : '#fef3c7';
  return <span style={{ ...S.badge, background: bg, color, fontWeight: 700 }}>{value}%</span>;
};

const ConnTypeBadge = ({ type }) => {
  const cfg = {
    wifi_sucursal:  { bg: '#cffafe', fg: '#0e7490', label: 'WiFi sucursal' },
    samsara:        { bg: '#ede9fe', fg: '#5b21b6', label: 'Samsara' },
    datos_celular:  { bg: '#fef3c7', fg: '#92400e', label: 'SIM/Datos' },
    desconocido:    { bg: '#f3f4f6', fg: '#374151', label: '?' },
  }[type || 'desconocido'];
  return <span style={{ ...S.badge, background: cfg.bg, color: cfg.fg }}>{cfg.label}</span>;
};

const S = {
  container: { padding: 24, maxWidth: 1400, margin: '0 auto' },
  header: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 700, margin: 0, display:'flex', alignItems:'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  refreshBtn: { padding: '8px 12px', background:'white', border:'1px solid #e5e7eb',
                borderRadius:8, cursor:'pointer' },
  tabs: { display:'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: { padding:'8px 16px', borderRadius:8, border:'1px solid #e5e7eb',
         background:'white', cursor:'pointer', fontSize:13,
         display:'flex', alignItems:'center', gap:6 },
  tabActive: { background:'#3b82f6', color:'white', borderColor:'#3b82f6' },
  card: { background:'white', padding:20, borderRadius:8, marginBottom:16,
          boxShadow:'0 1px 2px rgba(0,0,0,0.05)' },
  help: { color:'#6b7280', fontSize:13 },
  input: { padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:6, fontSize:14 },
  btnPrimary: { padding:'8px 16px', background:'#3b82f6', color:'white',
                border:'none', borderRadius:6, cursor:'pointer', fontSize:14,
                display:'flex', alignItems:'center', gap:6 },
  btnDanger: { padding:'6px 10px', background:'#fee2e2', color:'#991b1b',
               border:'none', borderRadius:6, cursor:'pointer' },
  btnAccept: { padding:'6px 12px', background:'#d1fae5', color:'#065f46',
               border:'none', borderRadius:6, cursor:'pointer',
               display:'flex', alignItems:'center', gap:4, fontSize:13 },
  btnIgnore: { padding:'6px 10px', background:'#f3f4f6', color:'#374151',
               border:'none', borderRadius:6, cursor:'pointer' },
  tableWrap: { overflow:'auto' },
  table: { width:'100%', borderCollapse:'collapse', fontSize:14 },
  th: { padding:'10px 12px', textAlign:'left', background:'#f9fafb',
        borderBottom:'1px solid #e5e7eb', fontSize:11,
        fontWeight:600, color:'#374151', textTransform:'uppercase' },
  td: { padding:'10px 12px', borderBottom:'1px solid #f3f4f6' },
  empty: { padding:40, textAlign:'center', color:'#9ca3af' },
  badge: { display:'inline-flex', alignItems:'center', gap:4,
           padding:'3px 8px', borderRadius:4, fontSize:12, fontWeight:500 },
  devBadge: { display:'inline-flex', justifyContent:'center', minWidth:30,
              padding:'3px 8px', borderRadius:4, background:'#dbeafe',
              color:'#1e40af', fontWeight:700, fontSize:13 },
  resultBox: { marginTop:16, padding:16, background:'#f9fafb', borderRadius:8 },
  resultRow: { display:'flex', alignItems:'center', gap:10, marginBottom:8 },
  resultLabel: { color:'#6b7280', minWidth:120 },
};

export default SamsaraV2;
